import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import FeedbackDialog from './FeedbackDialog';
import { API_BASE_URL } from '../utils/api';
import { tokens, fonts } from '../theme';
import { STEPS, trackStep } from '../utils/tracking';
import site, { usePageMeta } from '../content';

// Set axios defaults for CSRF protection with Django
axios.defaults.xsrfCookieName = 'csrftoken';
axios.defaults.xsrfHeaderName = 'X-CSRFToken';

// Poll interval in milliseconds
const POLL_INTERVAL = 3000;

// Maximum file size (25MB in bytes)
const MAX_FILE_SIZE = 25 * 1024 * 1024;  // 25MB

const STEP_LABELS = ['Document', 'Old logo', 'New logo'];

// Helper function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to validate file size
const validateFileSize = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    throw new Error(`This file is ${formatFileSize(file.size)}. The limit is ${formatFileSize(MAX_FILE_SIZE)}.`);
  }
};

const StepIndicator = ({ active }) => (
  <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', gap: { xs: 1, sm: 1.5 }, mb: 4 }}>
    {STEP_LABELS.map((label, i) => {
      const done = i < active;
      const current = i === active;
      return (
        <Box
          component="li"
          key={label}
          aria-current={current ? 'step' : undefined}
          sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}
        >
          <Box sx={{ height: 3, borderRadius: 2, backgroundColor: done || current ? tokens.accent : tokens.line }} />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, fontSize: '0.875rem', color: current ? tokens.text : tokens.muted, fontWeight: current ? 600 : 400 }}>
            {done ? <CheckIcon sx={{ fontSize: 16, color: tokens.accent }} /> : <span>{i + 1}.</span>}
            {label}
          </Box>
        </Box>
      );
    })}
  </Box>
);

// A drop zone that behaves like a button (keyboard + click + drag and drop).
const DropZone = ({ icon, title, hint, accept, onFile, disabled }) => {
  const inputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);

  const open = () => !disabled && inputRef.current && inputRef.current.click();

  return (
    <Box
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={open}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
      onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (!disabled && e.dataTransfer.files && e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]);
      }}
      sx={{
        border: `1.5px dashed ${dragOver ? tokens.accent : tokens.line}`,
        borderRadius: 3,
        backgroundColor: dragOver ? 'rgba(140, 158, 255, 0.08)' : tokens.ink,
        py: { xs: 6, sm: 8 },
        px: 3,
        textAlign: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': { borderColor: disabled ? tokens.line : tokens.muted },
        '&:focus-visible': { outline: `2px solid ${tokens.accentStrong}`, outlineOffset: 2 },
      }}
    >
      <Box sx={{ color: tokens.accent, mb: 1.5 }}>{icon}</Box>
      <Typography variant="h3" component="p" sx={{ mb: 0.75 }}>
        {dragOver ? 'Drop to upload' : title}
      </Typography>
      <Typography variant="body2" color="text.secondary">{hint}</Typography>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept={accept}
        onChange={(e) => {
          if (e.target.files && e.target.files[0]) onFile(e.target.files[0]);
          e.target.value = '';
        }}
      />
    </Box>
  );
};

// Bottom bar: secondary action on the left, primary on the right.
const Actions = ({ left, right }) => (
  <Box
    sx={{
      mt: 4,
      pt: 3,
      borderTop: `1px solid ${tokens.line}`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 2,
    }}
  >
    <Box>{left}</Box>
    <Box>{right}</Box>
  </Box>
);

const FileUpload = () => {
  usePageMeta(site.upload);
  const location = useLocation();
  const navigate = useNavigate();
  const initialFileHandled = useRef(false);

  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [documentId, setDocumentId] = useState(null);
  const [documentStatus, setDocumentStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(new Set());
  const [extractedImages, setExtractedImages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [selectedOldLogo, setSelectedOldLogo] = useState(null);
  const [showNewLogoUpload, setShowNewLogoUpload] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Restore feedback state from localStorage
  useEffect(() => {
    if (documentId) {
      const hasPendingFeedback = localStorage.getItem(`feedback_pending_${documentId}`);
      if (hasPendingFeedback === 'true') {
        setShowFeedback(true);
        setFeedbackGiven(prev => new Set([...prev, documentId]));
      }
    }
  }, [documentId]);

  // Update isProcessing based on documentStatus
  useEffect(() => {
    if (documentStatus === 'completed' || documentStatus === 'failed') {
      setIsProcessing(false);
    }
  }, [documentStatus]);

  // A file dropped or chosen on the landing page arrives via router state
  useEffect(() => {
    const incoming = location.state && location.state.file;
    if (incoming && !initialFileHandled.current) {
      initialFileHandled.current = true;
      navigate(location.pathname, { replace: true, state: null });
      handleDocumentChosen(incoming);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFeedbackClose = () => {
    if (documentId) {
      localStorage.removeItem(`feedback_pending_${documentId}`);
    }
    setShowFeedback(false);
  };

  const handleDocumentChosen = (selectedFile) => {
    const ext = selectedFile.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx', 'pptx'].includes(ext)) {
      setError('This file type isn\'t supported. Upload a PDF, Word (.docx) or PowerPoint (.pptx) file.');
      return;
    }
    try {
      validateFileSize(selectedFile);
      setFile(selectedFile);
      setError(null);
      handleUpload(selectedFile);
    } catch (err) {
      setError(err.message);
    }
  };

  // Poll for document status
  const pollDocumentStatus = async (id) => {
    if (!id) return;

    try {
      const response = await axios.get(`${API_BASE_URL}/documents/${id}/`);
      const { status, error_message } = response.data;

      setDocumentStatus(status);

      if (status === 'completed') {
        setIsPolling(false);
        setUploading(false);
        await handleDownload();
      } else if (status === 'failed') {
        setError(error_message || 'The logo couldn\'t be replaced in this file. Try again, or pick a different image.');
        setIsPolling(false);
        setUploading(false);
      } else if (status === 'processing' || status === 'pending') {
        setTimeout(() => pollDocumentStatus(id), POLL_INTERVAL);
      }
    } catch (err) {
      console.error("Error polling for status:", err);
      setError("Lost contact with the server while processing. Check your connection and try again.");
      setIsPolling(false);
      setUploading(false);
      setIsProcessing(false);
    }
  };

  // Start polling when document ID is set
  useEffect(() => {
    if (documentId && isPolling) {
      pollDocumentStatus(documentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, isPolling]);

  // Handle initial document upload
  const handleUpload = async (uploadFile) => {
    setUploading(true);
    setError(null);
    setDocumentStatus(null);
    setDocumentId(null);
    setFeedbackGiven(new Set());
    setExtractedImages([]);
    setSelectedIndex(null);
    setSelectedOldLogo(null);
    setShowNewLogoUpload(false);

    const formData = new FormData();
    formData.append('document', uploadFile);

    try {
      const response = await axios.post(`${API_BASE_URL}/extract-images/`, formData);

      if (response.data.images && response.data.images.length > 0) {
        setExtractedImages(response.data.images);
        trackStep(STEPS.DOCUMENT_UPLOADED);
      } else {
        setError("We didn't find any images in this document. Check that the logo is an image, not text.");
        setFile(null);
      }
    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.error || "Upload failed. Check your connection and try again.");
      setFile(null);
    } finally {
      setUploading(false);
    }
  };

  const handleNewLogoChosen = (logoFile) => {
    if (!logoFile.type.startsWith('image/')) {
      setError('The new logo must be an image file, like PNG or JPG.');
      return;
    }
    try {
      validateFileSize(logoFile);
    } catch (err) {
      setError(err.message);
      return;
    }
    handleReplace(logoFile);
  };

  // Send the document, old logo and new logo for processing
  const handleReplace = async (logoFile) => {
    if (!file || !selectedOldLogo || !logoFile) return;
    setError(null);
    setIsProcessing(true);
    trackStep(STEPS.PROCESSING);
    setUploading(true);
    setDocumentStatus('pending');

    try {
      // Convert base64 to blob for old logo
      const base64Data = selectedOldLogo.split(',')[1];
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const oldLogoBlob = new Blob([byteArray], { type: 'image/png' });

      const formData = new FormData();
      formData.append('original_document', file);
      formData.append('old_logo', oldLogoBlob, 'selected_logo.png');
      formData.append('new_logo', logoFile);

      const response = await axios.post(`${API_BASE_URL}/documents/`, formData);

      if (response.data) {
        setDocumentId(response.data.access_token);
        setDocumentStatus('processing');
        setIsPolling(true);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Processing couldn\'t start. Try again.');
      setIsProcessing(false);
      setDocumentStatus(null);
    } finally {
      setUploading(false);
    }
  };

  // Handle document download
  const handleDownload = async () => {
    if (!documentId) return;

    try {
      const checkResponse = await axios.get(`${API_BASE_URL}/documents/${documentId}/download/`);

      if (checkResponse.data.download_url) {
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        document.body.appendChild(iframe);
        iframe.src = checkResponse.data.download_url;
        trackStep(STEPS.DOWNLOADED);

        if (!feedbackGiven.has(documentId)) {
          localStorage.setItem(`feedback_pending_${documentId}`, 'true');
          setShowFeedback(true);
          setFeedbackGiven(prev => new Set([...prev, documentId]));
        }

        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 5000);
      } else {
        const fileResponse = await axios.get(`${API_BASE_URL}/documents/${documentId}/download/`, {
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(fileResponse.data);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', '');
        document.body.appendChild(link);
        link.click();
        trackStep(STEPS.DOWNLOADED);
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        if (!feedbackGiven.has(documentId)) {
          localStorage.setItem(`feedback_pending_${documentId}`, 'true');
          setShowFeedback(true);
          setFeedbackGiven(prev => new Set([...prev, documentId]));
        }
      }
    } catch (err) {
      console.error("Download error:", err);
      setError(err.response?.data?.detail || "The download didn't start. Try again.");
    }
  };

  const startOver = () => {
    setFile(null);
    setError(null);
    setDocumentId(null);
    setDocumentStatus(null);
    setIsPolling(false);
    setIsProcessing(false);
    setExtractedImages([]);
    setSelectedIndex(null);
    setSelectedOldLogo(null);
    setShowNewLogoUpload(false);
  };

  // Which screen to show
  let stage;
  if (documentStatus === 'completed') stage = 'done';
  else if (isProcessing) stage = 'processing';
  else if (showNewLogoUpload) stage = 'newLogo';
  else if (extractedImages.length > 0) stage = 'pick';
  else if (uploading) stage = 'extracting';
  else stage = 'document';

  const activeStep = { document: 0, extracting: 0, pick: 1, newLogo: 2, processing: 2, done: 3 }[stage];

  const fileChip = file && (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: 2, backgroundColor: tokens.raised, maxWidth: '100%' }}>
      <UploadFileIcon sx={{ fontSize: 18, color: tokens.muted }} />
      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>{file.name}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{formatFileSize(file.size)}</Typography>
    </Box>
  );

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ fontSize: 'clamp(1.9rem, 4vw, 2.6rem)', mb: 1 }}>
        {site.upload.h1}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 4 }}>
        {site.upload.intro}
      </Typography>

      <Paper sx={{ p: { xs: 2.5, sm: 4 }, borderRadius: 4 }}>
        <StepIndicator active={activeStep} />

        {error && (
          <Alert severity="error" variant="outlined" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: choose a document */}
        {stage === 'document' && (
          <DropZone
            icon={<UploadFileIcon sx={{ fontSize: 40 }} />}
            title="Upload a document"
            hint="Click or drop a PDF, Word or PowerPoint file, up to 25 MB"
            accept=".pdf,.docx,.pptx"
            onFile={handleDocumentChosen}
          />
        )}

        {stage === 'extracting' && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress size={36} />
            <Typography variant="h3" component="p" sx={{ mt: 2.5, mb: 1 }}>Finding images in your document</Typography>
            {fileChip}
          </Box>
        )}

        {/* Step 2: pick the old logo */}
        {stage === 'pick' && (
          <>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', justifyContent: 'space-between', gap: 1.5, mb: 2.5 }}>
              <Typography variant="h3" component="h2">Click the old logo</Typography>
              <Typography variant="body2" color="text.secondary">
                {extractedImages.length} {extractedImages.length === 1 ? 'image' : 'images'} found
              </Typography>
            </Box>
            <Box
              role="radiogroup"
              aria-label="Images in your document"
              sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' }, gap: 1.5 }}
            >
              {extractedImages.map((image, index) => {
                const selected = selectedIndex === index;
                return (
                  <Box
                    component="button"
                    type="button"
                    role="radio"
                    aria-checked={selected}
                    key={index}
                    onClick={() => { setSelectedIndex(index); setSelectedOldLogo(image); setShowNewLogoUpload(true); trackStep(STEPS.OLD_LOGO_PICKED); }}
                    sx={{
                      position: 'relative',
                      aspectRatio: '1 / 1',
                      p: 1.5,
                      borderRadius: 2,
                      cursor: 'pointer',
                      backgroundColor: '#FFFFFF',
                      border: 'none',
                      outline: selected ? `3px solid ${tokens.accent}` : `1px solid ${tokens.line}`,
                      outlineOffset: selected ? 2 : 0,
                      transition: 'outline-color 0.15s',
                      '&:hover': { outline: `3px solid ${selected ? tokens.accent : tokens.muted}`, outlineOffset: 2 },
                      '&:focus-visible': { outline: `3px solid ${tokens.accentStrong}`, outlineOffset: 2 },
                    }}
                  >
                    <img
                      src={image}
                      alt={`Option ${index + 1} from your document`}
                      loading="lazy"
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    />
                    {selected && (
                      <Box sx={{ position: 'absolute', top: 8, right: 8, width: 24, height: 24, borderRadius: '50%', backgroundColor: tokens.accent, color: tokens.onAccent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CheckIcon sx={{ fontSize: 16 }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            <Actions
              left={<Button variant="text" onClick={startOver}>Use a different document</Button>}
              right={null}
            />
          </>
        )}

        {/* Step 3: add the new logo; processing starts right away */}
        {stage === 'newLogo' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2.5 }}>
              <Box sx={{ width: 56, height: 56, flexShrink: 0, borderRadius: 1.5, backgroundColor: '#FFFFFF', p: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={selectedOldLogo} alt="Old logo" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
              <Typography variant="h3" component="h2">What should replace it?</Typography>
            </Box>
            <DropZone
              icon={<AddPhotoAlternateIcon sx={{ fontSize: 40 }} />}
              title="Upload the new logo"
              hint="Click or drop an image. We replace the logo and download your file right away."
              accept="image/*"
              onFile={handleNewLogoChosen}
            />
            <Actions
              left={
                <Button variant="text" onClick={() => setShowNewLogoUpload(false)}>
                  Back
                </Button>
              }
              right={null}
            />
          </>
        )}

        {stage === 'processing' && (
          <Box sx={{ py: 7, textAlign: 'center', maxWidth: 420, mx: 'auto' }}>
            <Typography variant="h3" component="p" sx={{ mb: 1 }}>
              {documentStatus === 'processing' ? 'Replacing the logo' : 'Uploading your files'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This usually takes under a minute. Keep this tab open.
            </Typography>
            <LinearProgress sx={{ height: 6, borderRadius: 3, backgroundColor: tokens.line }} />
            <Box sx={{ mt: 3 }}>{fileChip}</Box>
          </Box>
        )}

        {stage === 'done' && (
          <>
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Box sx={{ width: 56, height: 56, mx: 'auto', mb: 2.5, borderRadius: '50%', backgroundColor: 'rgba(95, 211, 160, 0.14)', color: tokens.success, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <CheckIcon sx={{ fontSize: 30 }} />
              </Box>
              <Typography sx={{ fontFamily: fonts.display, fontWeight: 650, fontSize: '1.5rem', mb: 1 }}>
                Logo replaced
              </Typography>
              <Typography color="text.secondary">
                Your file is downloading. Check your Downloads folder.
              </Typography>
            </Box>
            <Actions
              left={null}
              right={<Button variant="contained" onClick={startOver}>Replace another logo</Button>}
            />
          </>
        )}
      </Paper>

      <FeedbackDialog
        open={showFeedback}
        onClose={handleFeedbackClose}
      />
    </Box>
  );
};

export default FileUpload;
